import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isSecretaryContextUser, getCurrentUser } from '../authStorage';
import { startMeetingRoom, getActiveMeetingRoom } from '../meetingRoomApi';
import {
  fetchMeetingById,
  fetchMeetingWorkspace,
  resendMeetingInvitation,
  saveMeetingAgenda,
  saveMeetingAttachments,
  saveMeetingAttendance,
  saveMeetingMinutes,
  saveMeetingTasks,
} from '../meetingsApi';
import { openGoogleCalendar } from '../calendarUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';

const MENU_ITEMS = [
  { key: 'basic',       icon: 'calendar' },
  { key: 'members',     icon: 'user'     },
  { key: 'agenda',      icon: 'report'   },
  { key: 'tasks',       icon: 'settings' },
  { key: 'attendance',  icon: 'board'    },
  { key: 'attachments', icon: 'file'     },
  { key: 'minutes',     icon: 'edit'     },
];

const MENU_LABEL_KEYS = {
  basic:       'meetings.basic',
  members:     'meetings.summaryMembers',
  agenda:      'meetings.agendaTab',
  tasks:       'meetings.tasksTab',
  attendance:  'meetings.attendanceTab',
  attachments: 'meetings.attachmentsTab',
  minutes:     'meetings.minutesTab',
};

export default function MeetingWorkspacePage() {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const meetingId = Number(idParam);
  const invalidId = !Number.isFinite(meetingId) || meetingId < 1;

  const dateLocale = language === 'ar' || language === 'ur' ? 'ar-SA-u-ca-gregory'
    : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US';

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(!invalidId);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('basic');
  const [agendaRows, setAgendaRows] = useState([]);
  const [taskRows, setTaskRows] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attachmentRows, setAttachmentRows] = useState([]);
  const [minutes, setMinutes] = useState({ summary: '', decisions: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [addAgendaOpen, setAddAgendaOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addAttachmentOpen, setAddAttachmentOpen] = useState(false);
  const [agendaDraft, setAgendaDraft] = useState({
    title: '', description: '', durationMinutes: 15, notes: '',
    actionView: true, actionDiscuss: false, actionInstruct: false,
  });
  const [taskDraft, setTaskDraft] = useState({
    title: '', description: '', assigneeName: '', agendaItemId: '',
    status: 'pending', dueDate: '', attachments: [], notifyUser: true,
  });
  const [attachmentDraft, setAttachmentDraft] = useState({ name: '', kind: 'general', url: '' });
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState({ text: '', ok: true });
  const [roomLoading, setRoomLoading] = useState(false);
  const [roomError, setRoomError]   = useState('');
  const [copyMsg, setCopyMsg]       = useState('');

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setError(t('common.noPerm')); setLoading(false); return; }
    if (invalidId) { setError(t('meetings.invalidId')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [row, workspace] = await Promise.all([
          fetchMeetingById(meetingId),
          fetchMeetingWorkspace(meetingId),
        ]);
        if (cancelled) return;
        setItem(row);
        const fallbackAgenda = String(row.agenda || '')
          .split('\n').map((x) => x.trim()).filter(Boolean)
          .map((x, i) => ({ id: `a-${i + 1}`, title: x, description: '' }));
        setAgendaRows((workspace.agendaItems || []).length ? workspace.agendaItems : fallbackAgenda);
        setTaskRows(workspace.tasks || []);
        const fallbackAttendance = (Array.isArray(row.invitees) ? row.invitees : []).map((m) => ({
          targetType: m.targetType, targetId: m.targetId, status: 'pending', note: '',
        }));
        setAttendanceRows((workspace.attendance || []).length ? workspace.attendance : fallbackAttendance);
        setAttachmentRows(workspace.attachments || []);
        setMinutes(workspace.minutes || { summary: '', decisions: '' });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('meetings.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, meetingId, invalidId]); // eslint-disable-line react-hooks/exhaustive-deps

  const meetingDate = item?.startAtUtc ? new Date(item.startAtUtc) : null;
  const invitees = Array.isArray(item?.invitees) ? item.invitees : [];

  const addAgendaItem = () => {
    if (!agendaDraft.title.trim()) return;
    setAgendaRows((p) => [...p, {
      id: `a-${Date.now()}`,
      title: agendaDraft.title.trim(),
      description: agendaDraft.description.trim(),
      durationMinutes: Number(agendaDraft.durationMinutes) > 0 ? Number(agendaDraft.durationMinutes) : 15,
      notes: agendaDraft.notes.trim() || null,
      actionView: !!agendaDraft.actionView,
      actionDiscuss: !!agendaDraft.actionDiscuss,
      actionInstruct: !!agendaDraft.actionInstruct,
    }]);
    setAgendaDraft({ title: '', description: '', durationMinutes: 15, notes: '', actionView: true, actionDiscuss: false, actionInstruct: false });
    setAddAgendaOpen(false);
  };
  const addTaskItem = () => {
    if (!taskDraft.title.trim()) return;
    setTaskRows((p) => [...p, {
      id: `t-${Date.now()}`,
      title: taskDraft.title.trim(),
      description: taskDraft.description.trim() || null,
      assigneeName: taskDraft.assigneeName.trim(),
      agendaItemId: taskDraft.agendaItemId || null,
      dueDate: taskDraft.dueDate || null,
      status: taskDraft.status || 'pending',
      attachments: Array.isArray(taskDraft.attachments) ? taskDraft.attachments : [],
      notifyUser: !!taskDraft.notifyUser,
    }]);
    setTaskDraft({ title: '', description: '', assigneeName: '', agendaItemId: '', status: 'pending', dueDate: '', attachments: [], notifyUser: true });
    setAddTaskOpen(false);
  };
  const addAttachmentItem = () => {
    if (!attachmentDraft.name.trim()) return;
    setAttachmentRows((p) => [...p, {
      id: `f-${Date.now()}`,
      name: attachmentDraft.name.trim(),
      kind: attachmentDraft.kind || 'general',
      url: attachmentDraft.url.trim() || null,
    }]);
    setAttachmentDraft({ name: '', kind: 'general', url: '' });
    setAddAttachmentOpen(false);
  };
  const upsertAttendance = (targetType, targetId, status) => {
    const key = `${targetType}:${targetId}`;
    setAttendanceRows((p) => {
      const idx = p.findIndex((x) => `${x.targetType}:${x.targetId}` === key);
      if (idx === -1) return [...p, { targetType, targetId, status, note: '' }];
      return p.map((x, i) => (i === idx ? { ...x, status } : x));
    });
  };
  const attendanceStatus = (targetType, targetId) => (
    attendanceRows.find((x) => x.targetType === targetType && x.targetId === targetId)?.status || 'pending'
  );
  const saveCurrentTab = async () => {
    if (!item || saving) return;
    setSaveMsg('');
    setSaving(true);
    try {
      if (tab === 'agenda')      await saveMeetingAgenda(item.id, agendaRows);
      if (tab === 'tasks')       await saveMeetingTasks(item.id, taskRows);
      if (tab === 'attendance')  await saveMeetingAttendance(item.id, attendanceRows);
      if (tab === 'attachments') await saveMeetingAttachments(item.id, attachmentRows);
      if (tab === 'minutes')     await saveMeetingMinutes(item.id, minutes);
      setSaveMsg(t('meetings.saved'));
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : t('meetings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    if (resending || !item) return;
    setResending(true);
    setResendMsg({ text: '', ok: true });
    try {
      const result = await resendMeetingInvitation(item.id);
      setResendMsg({ text: `${t('meetings.resendOk')} (${result.sentCount})`, ok: true });
    } catch (e) {
      setResendMsg({ text: e instanceof Error ? e.message : t('meetings.resendFail'), ok: false });
    } finally {
      setResending(false);
    }
  };

  const statusBadge = String(item?.status || 'draft').toLowerCase() === 'published'
    ? { text: t('meetings.published'), bg: '#e8f5e9', color: '#2e7d32' }
    : { text: t('common.draft'), bg: '#fff3e0', color: '#ef6c00' };

  const currentUser = getCurrentUser();
  // Host = meeting creator OR any secretary-context user (can start rooms)
  const isHost = item && currentUser && (
    item.createdByUserId === currentUser.id || isSecretaryContextUser()
  );

  async function handleStartMeeting() {
    setRoomLoading(true);
    setRoomError('');
    try {
      const room = await startMeetingRoom(item.id);
      navigate(`/meeting-room/${room.roomToken}`);
    } catch (e) {
      setRoomError(e.message);
    } finally {
      setRoomLoading(false);
    }
  }

  async function handleJoinMeeting() {
    setRoomLoading(true);
    setRoomError('');
    try {
      const room = await getActiveMeetingRoom(item.id);
      navigate(`/meeting-room/${room.roomToken}`);
    } catch (e) {
      setRoomError('No active room yet. The host has not started the meeting.');
    } finally {
      setRoomLoading(false);
    }
  }

  // Creates the room if needed (host) and copies its shareable link — without joining the call.
  async function handleCopyLink() {
    setRoomLoading(true);
    setRoomError('');
    setCopyMsg('');
    try {
      const room = isHost ? await startMeetingRoom(item.id) : await getActiveMeetingRoom(item.id);
      const url = `${window.location.origin}/meeting-room/${room.roomToken}`;
      await navigator.clipboard.writeText(url);
      setCopyMsg(t('room.linkCopied'));
      window.setTimeout(() => setCopyMsg(''), 2500);
    } catch (e) {
      setRoomError(e instanceof Error ? e.message : t('room.copyLinkFailed'));
    } finally {
      setRoomLoading(false);
    }
  }

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <button type="button" className="btn btn--ghost" onClick={() => navigate(`${basePath}/meetings`)}>
                <Icon name="chevron-right" size={14} /> {t('common.back')}
              </button>
              <span className="list-page-card__title"><Icon name="calendar" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('meetings.pageTitle')}</span>
            </div>
            <span className="list-page-card__sub">{item?.title || ''}</span>
          </div>
          {item && (
            <div className="list-page-card__actions">
              <button type="button" className="btn btn--outline" onClick={handleResend} disabled={resending}>
                <Icon name="bell" size={14} color="#0EA5A0" /> {resending ? t('meetings.resending') : t('meetings.resend')}
              </button>
              <button type="button" className="btn btn--outline" onClick={() => navigate(`${basePath}/meetings/${item.id}/edit`)}>
                <Icon name="edit" size={14} color="#02c3a6" /> {t('meetings.editMeeting')}
              </button>
              <button type="button" className="btn btn--outline" onClick={handleCopyLink} disabled={roomLoading}>
                <Icon name="link" size={14} color="#0EA5A0" /> {t('room.copyLink')}
              </button>
              <button type="button" className="btn btn--outline" onClick={() => openGoogleCalendar(item)}>
                <Icon name="calendar" size={14} color="#0EA5A0" /> {t('meetings.addToCalendar') || 'إضافة للتقويم'}
              </button>
              {isHost ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleStartMeeting}
                  disabled={roomLoading}
                  style={{ background: '#02c3a6', color: '#fff', border: 'none' }}
                >
                  {roomLoading ? t('room.starting') : t('room.startMeeting')}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleJoinMeeting}
                  disabled={roomLoading}
                  style={{ background: '#00334a', color: '#fff', border: 'none' }}
                >
                  {roomLoading ? t('room.joining') : t('room.joinMeeting')}
                </button>
              )}
            </div>
          )}
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {roomError && <div className="list-page-card__banner list-page-card__banner--error" style={{ cursor: 'pointer' }} onClick={() => setRoomError('')}>{roomError}</div>}
        {copyMsg && <div className="list-page-card__banner list-page-card__banner--success">{copyMsg}</div>}
        {loading && <div className="list-page-card__loading">{t('common.loading')}</div>}
        {resendMsg.text && (
          <div className={`list-page-card__banner${resendMsg.ok ? '' : ' list-page-card__banner--error'}`} style={{ cursor: 'pointer' }} onClick={() => setResendMsg({ text: '', ok: true })}>
            {resendMsg.text}
          </div>
        )}

      {!loading && item && (
        <div className="table-card" style={{ padding: '12px', border: '1px solid #eceff1' }}>
          <div style={{ display: 'grid', gridTemplateColumns: direction === 'rtl' ? '240px 1fr' : '1fr 240px', gap: 12 }}>
            <div style={{ border: '1px solid #eceff1', borderRadius: 12, padding: 8, background: '#fff' }}>
              {MENU_ITEMS.map((x) => {
                const active = tab === x.key;
                return (
                  <button
                    key={x.key}
                    type="button"
                    onClick={() => setTab(x.key)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      border: `1px solid ${active ? '#d1c4e9' : 'transparent'}`,
                      background: active ? '#f3e5f5' : '#fff',
                      color: active ? '#6a1b9a' : '#455a64',
                      borderRadius: 10,
                      padding: '10px 12px',
                      marginBottom: 6,
                      cursor: 'pointer',
                      fontWeight: active ? 800 : 600,
                    }}
                  >
                    <span>{t(MENU_LABEL_KEYS[x.key])}</span>
                    <Icon name={x.icon} size={14} color={active ? '#7e57c2' : '#90a4ae'} />
                  </button>
                );
              })}
            </div>

            <div style={{ border: '1px solid #eceff1', borderRadius: 12, padding: '14px 16px', background: '#fff' }}>
          {tab === 'basic' && (
            <div className="mform">
              <div className="mform__row mform__row--4">
                <div className="mform__field"><label className="mform__label">{t('meetings.meetingLabel')}</label><input className="mform__input" disabled value={item.title || ''} /></div>
                <div className="mform__field"><label className="mform__label">{t('meetings.goalLabel')}</label><input className="mform__input" disabled value={item.goal || ''} /></div>
                <div className="mform__field"><label className="mform__label">{t('common.date')}</label><input className="mform__input" disabled value={meetingDate ? meetingDate.toLocaleDateString(dateLocale) : ''} /></div>
                <div className="mform__field"><label className="mform__label">{t('common.status')}</label><div><span style={{ padding: '4px 8px', borderRadius: 999, background: statusBadge.bg, color: statusBadge.color, fontWeight: 700 }}>{statusBadge.text}</span></div></div>
              </div>
              <div className="mform__row mform__row--4">
                <div className="mform__field"><label className="mform__label">{t('meetings.locationLabel')}</label><input className="mform__input" disabled value={item.location || ''} /></div>
                <div className="mform__field"><label className="mform__label">{t('meetings.attendanceTypeLabel')}</label><input className="mform__input" disabled value={item.attendanceType === 'virtual' ? t('meetings.virtual') : t('meetings.inPerson')} /></div>
                <div className="mform__field"><label className="mform__label">{t('meetings.boardLabel')}</label><input className="mform__input" disabled value={item.boardName || '—'} /></div>
                <div className="mform__field"><label className="mform__label">{t('meetings.committeeLabel')}</label><input className="mform__input" disabled value={item.committeeName || '—'} /></div>
              </div>
            </div>
          )}

          {tab === 'members' && (
            <div className="table-card" style={{ border: '1px solid #eef2f5' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('meetings.memberName')}</th><th>{t('meetings.memberTypeCol')}</th><th>{t('common.email')}</th><th>{t('common.phone')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invitees.map((m, i) => (
                    <tr key={`${m.targetType}-${m.targetId}-${i}`}>
                      <td>{m.displayName || '—'}</td>
                      <td>{m.targetType || '—'}</td>
                      <td>{m.subtitle || '—'}</td>
                      <td>—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {invitees.length === 0 && <div className="users-empty">{t('meetings.noneYet')}</div>}
            </div>
          )}

          {tab === 'agenda' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
                <button type="button" className="btn btn--outline" onClick={() => setAddAgendaOpen(true)}>{t('meetings.addItem')}</button>
                <button type="button" className="btn btn--ghost" onClick={() => setTab('tasks')}>{t('meetings.tasksTab')}</button>
              </div>
              <div className="table-card" style={{ border: '1px solid #eef2f5' }}>
                <table className="data-table">
                  <thead><tr><th>{t('meetings.titleField')}</th><th>{t('meetings.durationField')}</th><th>{t('meetings.actionsLbl')}</th></tr></thead>
                  <tbody>
                    {agendaRows.map((a) => (
                      <tr key={a.id}>
                        <td>{a.title}</td>
                        <td>{a.durationMinutes || 15}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {a.actionView && <span className="btn btn--ghost" style={{ padding: '2px 8px' }}>{t('meetings.actionView')}</span>}
                            {a.actionDiscuss && <span className="btn btn--ghost" style={{ padding: '2px 8px' }}>{t('meetings.actionDiscuss')}</span>}
                            {a.actionInstruct && <span className="btn btn--ghost" style={{ padding: '2px 8px' }}>{t('meetings.actionInstruct')}</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {agendaRows.length === 0 && <div className="users-empty">{t('meetings.noneYet')}</div>}
              </div>
            </>
          )}

          {tab === 'tasks' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
                <button type="button" className="btn btn--outline" onClick={() => setAddTaskOpen(true)}>{t('common.add')}</button>
              </div>
              <div className="table-card" style={{ border: '1px solid #eef2f5' }}>
                <table className="data-table">
                  <thead><tr><th>{t('meetings.titleField')}</th><th>{t('meetings.assignee')}</th><th>{t('meetings.agendaItemField')}</th><th>{t('meetings.dueDate')}</th><th>{t('common.status')}</th><th>{t('meetings.taskAttachments')}</th></tr></thead>
                  <tbody>
                    {taskRows.map((x) => (
                      <tr key={x.id}>
                        <td>{x.title}</td>
                        <td>{x.assigneeName || '—'}</td>
                        <td>{agendaRows.find((a) => a.id === x.agendaItemId)?.title || '—'}</td>
                        <td>{x.dueDate || '—'}</td>
                        <td>
                          <button type="button" className="btn btn--ghost" onClick={() => setTaskRows((p) => p.map((tRow) => (tRow.id === x.id ? { ...tRow, status: tRow.status === 'pending' ? 'in_progress' : tRow.status === 'in_progress' ? 'done' : 'pending' } : tRow)))}>
                            {x.status === 'done' ? t('meetings.taskDone') : x.status === 'in_progress' ? t('meetings.taskInProgress') : t('meetings.taskPending')}
                          </button>
                        </td>
                        <td>{Array.isArray(x.attachments) && x.attachments.length ? x.attachments.length : 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {taskRows.length === 0 && <div className="users-empty">{t('meetings.noneYet')}</div>}
              </div>
            </>
          )}

          {tab === 'attendance' && (
            <div className="table-card" style={{ border: '1px solid #eef2f5' }}>
              <table className="data-table">
                <thead><tr><th>{t('meetings.memberName')}</th><th>{t('common.status')}</th></tr></thead>
                <tbody>
                  {invitees.map((m, i) => {
                    const v = attendanceStatus(m.targetType, m.targetId);
                    return (
                      <tr key={`${m.targetType}:${m.targetId}-${i}`}>
                        <td>{m.displayName || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" className="btn btn--ghost" onClick={() => upsertAttendance(m.targetType, m.targetId, 'present')}>{t('meetings.present')}</button>
                            <button type="button" className="btn btn--ghost" onClick={() => upsertAttendance(m.targetType, m.targetId, 'absent')}>{t('meetings.absent')}</button>
                            <button type="button" className="btn btn--ghost" onClick={() => upsertAttendance(m.targetType, m.targetId, 'excused')}>{t('meetings.excuse')}</button>
                            <span style={{ color: '#78909c', fontSize: '.78rem' }}>{v === 'pending' ? t('meetings.pendingStatus') : v}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {invitees.length === 0 && <div className="users-empty">{t('meetings.noneYet')}</div>}
            </div>
          )}

          {tab === 'attachments' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button type="button" className="btn btn--outline" onClick={() => setAddAttachmentOpen(true)}>{t('common.add')}</button>
              </div>
              <div className="table-card" style={{ border: '1px solid #eef2f5' }}>
                <table className="data-table">
                  <thead><tr><th>{t('meetings.titleField')}</th><th>{t('meetings.kindField')}</th><th>{t('meetings.linkField')}</th></tr></thead>
                  <tbody>
                    {attachmentRows.map((x) => <tr key={x.id}><td>{x.name}</td><td>{x.kind || 'general'}</td><td>{x.url || '—'}</td></tr>)}
                  </tbody>
                </table>
                {attachmentRows.length === 0 && <div className="users-empty">{t('meetings.noneYet')}</div>}
              </div>
            </>
          )}

          {tab === 'minutes' && (
            <div className="mform">
              <div className="mform__field">
                <label className="mform__label">{t('meetings.summaryField')}</label>
                <textarea className="mform__textarea" rows={5} value={minutes.summary || ''} onChange={(e) => setMinutes((p) => ({ ...p, summary: e.target.value }))} />
              </div>
              <div className="mform__field">
                <label className="mform__label">{t('meetings.decisionsField')}</label>
                <textarea className="mform__textarea" rows={4} value={minutes.decisions || ''} onChange={(e) => setMinutes((p) => ({ ...p, decisions: e.target.value }))} />
              </div>
            </div>
          )}

          {(tab !== 'basic' && tab !== 'members') && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 12, gap: 8 }}>
              <button type="button" className="btn btn--primary" onClick={saveCurrentTab} disabled={saving}>{saving ? t('meetings.saving') : t('common.save')}</button>
              {saveMsg && <span style={{ color: '#0d47a1', alignSelf: 'center' }}>{saveMsg}</span>}
            </div>
          )}
            </div>
          </div>
        </div>
      )}
      </div>

      <Modal
        open={addAgendaOpen}
        onClose={() => setAddAgendaOpen(false)}
        title={t('meetings.addItem')}
        subtitle={t('meetings.addAgendaSubtitle')}
        size="lg"
      >
        <div className="mform" dir={direction}>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.itemTitleLabel')} <span>*</span></label>
            <input className="mform__input" value={agendaDraft.title} onChange={(e) => setAgendaDraft((p) => ({ ...p, title: e.target.value }))} placeholder={t('meetings.itemTitlePh')} />
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.itemDescLabel')}</label>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', gap: 10, padding: '8px 10px', borderBottom: '1px solid #e0e0e0', color: '#37474f' }}>
                <span>🅱</span><span>𝘐</span><span>🔗</span><span>🖼</span><span>H</span>
              </div>
              <textarea className="mform__textarea" rows={5} style={{ border: 'none', borderRadius: 0 }} value={agendaDraft.description} onChange={(e) => setAgendaDraft((p) => ({ ...p, description: e.target.value }))} placeholder={t('meetings.descPh')} />
            </div>
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.durationField')} <span>*</span></label>
            <input type="number" min="1" max="480" className="mform__input" value={agendaDraft.durationMinutes} onChange={(e) => setAgendaDraft((p) => ({ ...p, durationMinutes: e.target.value }))} />
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('common.notes')}</label>
            <textarea className="mform__textarea" rows={3} value={agendaDraft.notes} onChange={(e) => setAgendaDraft((p) => ({ ...p, notes: e.target.value }))} placeholder={t('meetings.notesPh')} />
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.actionsLbl')}</label>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={agendaDraft.actionView} onChange={(e) => setAgendaDraft((p) => ({ ...p, actionView: e.target.checked }))} /> {t('meetings.actionView')}</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={agendaDraft.actionDiscuss} onChange={(e) => setAgendaDraft((p) => ({ ...p, actionDiscuss: e.target.checked }))} /> {t('meetings.actionDiscuss')}</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={agendaDraft.actionInstruct} onChange={(e) => setAgendaDraft((p) => ({ ...p, actionInstruct: e.target.checked }))} /> {t('meetings.actionInstruct')}</label>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn--ghost" onClick={() => setAddAgendaOpen(false)}>{t('common.cancel')}</button>
            <button type="button" className="btn btn--primary" onClick={addAgendaItem}>{t('common.add')}</button>
          </div>
        </div>
      </Modal>

      <Modal
        open={addTaskOpen}
        onClose={() => setAddTaskOpen(false)}
        title={t('common.add')}
        subtitle={t('meetings.addTaskSubtitle')}
        size="lg"
      >
        <div className="mform">
          <div className="mform__field">
            <label className="mform__label">{t('meetings.taskNameReq')} <span>*</span></label>
            <input className="mform__input" value={taskDraft.title} onChange={(e) => setTaskDraft((p) => ({ ...p, title: e.target.value }))} placeholder={t('meetings.taskNamePh')} />
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('common.description')} <span>*</span></label>
            <textarea className="mform__textarea" rows={3} value={taskDraft.description} onChange={(e) => setTaskDraft((p) => ({ ...p, description: e.target.value }))} placeholder={t('meetings.descPh')} />
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.assigneePick')}</label>
            <select className="mform__select" value={taskDraft.assigneeName} onChange={(e) => setTaskDraft((p) => ({ ...p, assigneeName: e.target.value }))}>
              <option value="">{t('meetings.noAssignee')}</option>
              {invitees.map((x, i) => <option key={`${x.targetType}:${x.targetId}:${i}`} value={x.displayName || ''}>{x.displayName || `${x.targetType}:${x.targetId}`}</option>)}
            </select>
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.agendaItemField')}</label>
            <select className="mform__select" value={taskDraft.agendaItemId} onChange={(e) => setTaskDraft((p) => ({ ...p, agendaItemId: e.target.value }))}>
              <option value="">{t('meetings.noAgendaItem')}</option>
              {agendaRows.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </select>
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('common.status')} <span>*</span></label>
            <select className="mform__select" value={taskDraft.status} onChange={(e) => setTaskDraft((p) => ({ ...p, status: e.target.value }))}>
              <option value="pending">{t('meetings.taskPending')}</option>
              <option value="in_progress">{t('meetings.taskInProgress')}</option>
              <option value="done">{t('meetings.taskDone')}</option>
            </select>
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.dueDate')}</label>
            <input type="date" className="mform__input" value={taskDraft.dueDate} onChange={(e) => setTaskDraft((p) => ({ ...p, dueDate: e.target.value }))} />
          </div>
          <div className="mform__field">
            <label className="mform__label">{t('meetings.taskAttachments')}</label>
            <label className="btn btn--outline" style={{ display: 'inline-flex', width: 'fit-content' }}>
              {t('meetings.uploadFile')}
              <input type="file" multiple style={{ display: 'none' }} onChange={(e) => { const names = Array.from(e.target.files || []).map((f) => f.name); setTaskDraft((p) => ({ ...p, attachments: names })); }} />
            </label>
            {Array.isArray(taskDraft.attachments) && taskDraft.attachments.length > 0 && (
              <div style={{ marginTop: 6, color: '#607d8b', fontSize: '.82rem' }}>{taskDraft.attachments.join(', ')}</div>
            )}
          </div>
          <div className="mform__field">
            <label className="mform__label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={taskDraft.notifyUser} onChange={(e) => setTaskDraft((p) => ({ ...p, notifyUser: e.target.checked }))} />
              {t('meetings.notifyUser')}
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn--ghost" onClick={() => setAddTaskOpen(false)}>{t('common.cancel')}</button>
            <button type="button" className="btn btn--primary" onClick={addTaskItem}>{t('common.add')}</button>
          </div>
        </div>
      </Modal>

      <Modal
        open={addAttachmentOpen}
        onClose={() => setAddAttachmentOpen(false)}
        title={t('common.add')}
        subtitle={t('meetings.addAttachSubtitle')}
        size="sm"
      >
        <div className="mform">
          <div className="mform__field"><label className="mform__label">{t('meetings.titleField')}</label><input className="mform__input" value={attachmentDraft.name} onChange={(e) => setAttachmentDraft((p) => ({ ...p, name: e.target.value }))} /></div>
          <div className="mform__field"><label className="mform__label">{t('meetings.kindField')}</label><select className="mform__select" value={attachmentDraft.kind} onChange={(e) => setAttachmentDraft((p) => ({ ...p, kind: e.target.value }))}><option value="general">General</option><option value="agenda">Agenda</option><option value="minutes">Minutes</option></select></div>
          <div className="mform__field"><label className="mform__label">{t('meetings.linkField')}</label><input className="mform__input" value={attachmentDraft.url} onChange={(e) => setAttachmentDraft((p) => ({ ...p, url: e.target.value }))} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn--ghost" onClick={() => setAddAttachmentOpen(false)}>{t('common.cancel')}</button>
            <button type="button" className="btn btn--primary" onClick={addAttachmentItem}>{t('common.add')}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
